import MuleBadge from '../MuleBadge';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  ChatBubbleBottomCenterTextIcon,
  ExclamationTriangleIcon,
  HandRaisedIcon,
  MapIcon,
  PlusCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { Tooltip } from '../ui/Tooltip';
import DriverPhoneLink from '../DriverPhoneLink';
import { MoveCard } from './MoveCard';
import { CompactMoveCard } from './CompactMoveCard';
import { SuperCompactMoveCard } from './SuperCompactMoveCard';
import { GhostMoveCard } from '../GhostMoveCard';
import { formatMinutes, formatMinutesCompact, formatRequestAge, formatStartTime, groupConsecutiveLegs, nextDeliveryAppt } from './utils';
import { MoveRisk, DriverWithMoves, DisplaySettings, MoveGroup, DragState, ProjectedStatus, GhostSuggestion } from './types';
import {
  computeMoveProjections,
  getProjectedETA as getProjectedETAShared,
  isETAToDestination as isETAToDestShared,
  formatETA as formatETAShared,
} from './etaProjection';
import { computeRouteCascade } from './routeEtaCascade';
import { CompactHosWidget } from './CompactHosWidget';
import api from '../../services/api';
import { config } from '../../config/config';
import styles from './DriverLane.module.css';

/** Title on the header's start-time chip (first on-duty time from the Samsara HOS log). */
export const START_CHIP_TITLE = 'Start time: first on-duty transition in the Samsara HOS log for this day';

export interface DriverLaneProps {
  driver: DriverWithMoves;
  index?: number;
  showCompleted: boolean;
  displaySettings: DisplaySettings;

  /** Layout orientation: 'horizontal' = scrolling row (default), 'vertical' = stacked column */
  orientation?: 'horizontal' | 'vertical';

  /** Render prop for location map (keeps Google Maps dependency out of this component) */
  renderLocationMap?: (props: {
    driverLat: number;
    driverLng: number;
    originLat?: number | null;
    originLng?: number | null;
    destLat?: number | null;
    destLng?: number | null;
  }) => React.ReactNode;

  // All interaction callbacks are optional — when not provided, their UI triggers are hidden
  onProClick?: (pronumber: number, legnum: number) => void;
  /** When provided, each move card shows an open-in-drawer icon next to its PRO link */
  onProDetailsClick?: (pronumber: number) => void;
  onRemove?: (move: MoveRisk, group?: MoveGroup) => void;
  onQuickAssign?: (move: MoveRisk, targetUnitnumber: string) => void;
  onDragStart?: (e: React.DragEvent, move: MoveRisk, sourceDriver: string, group?: MoveGroup) => void;
  onDragEnd?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent, targetDriver: string) => void;
  onReorder?: (driverUnitnumber: string, newOrder: MoveGroup[]) => void;
  isDragTarget?: boolean;
  dragState?: DragState | null;
  openAvailableMovesDrawer?: (unitNumber: string, driverName: string, mapData?: {
    fromName: string;
    fromCityState: string;
    fromLat: number;
    fromLng: number;
    toName: string;
    toCityState: string;
    toLat: number;
    toLng: number;
    travelMinutes: number;
  }) => void;
  onOpenDeadheadMap?: (prevMove: MoveRisk, nextMove: MoveRisk, travelMinutes: number, driverUnit: string, driverName: string) => void;
  onGhostCardClick?: (suggestion: GhostSuggestion) => void;
  showGhostCards?: boolean;
  userEmail?: string;

  /** Hide the driver header row (name, stats, HOS). Use when embedding in an external container like a drawer. */
  hideHeader?: boolean;

  /** PRO the user came from (e.g. the Pro drawer this lane is embedded in) — its moves get a highlighted PRO link */
  highlightPronumber?: number;

  /** Whether this driver has unsaved priority changes */
  hasPendingReorder?: boolean;
  onApplyReorder?: (driverUnitnumber: string) => void;
  onCancelReorder?: (driverUnitnumber: string) => void;
  isToday?: boolean;
  hasDispatchRequest?: boolean;
  /** Clear this driver's open work request when we have nothing to give them */
  onAcknowledgeRequest?: (unitnumber: string) => void;
  /**
   * Add/edit the driver's status note (today's driverstatus.comments, the text the
   * header's comment icon shows on hover). When provided the icon becomes a button;
   * without it the icon stays hover-only.
   */
  onEditComment?: (unitnumber: string, driverName: string, currentComment: string | null) => void;
  /** The page-level date (YYYY-MM-DD). Used to detect when lane is viewing a different day. */
  pageDate?: string;
}

export function DriverLane({
  driver,
  showCompleted,
  displaySettings,
  orientation = 'horizontal',
  renderLocationMap,
  onProClick,
  onProDetailsClick,
  onRemove,
  onQuickAssign,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  onReorder,
  dragState,
  openAvailableMovesDrawer,
  onOpenDeadheadMap,
  onGhostCardClick,
  showGhostCards = true,
  hideHeader = false,
  highlightPronumber,
  hasPendingReorder = false,
  onApplyReorder,
  onCancelReorder,
  isToday = true,
  hasDispatchRequest = false,
  onAcknowledgeRequest,
  onEditComment,
  pageDate,
}: DriverLaneProps) {
  // Local state for drop target position during reorder
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);

  // Driver location map popup state
  const [showDriverLocationMap, setShowDriverLocationMap] = useState(false);

  // Day cycling state
  const [viewingDate, setViewingDate] = useState<string | null>(null);
  const [cycledDriverData, setCycledDriverData] = useState<DriverWithMoves | null>(null);
  const [cycleLoading, setCycleLoading] = useState(false);

  // Reset viewing date when page date changes
  useEffect(() => {
    setViewingDate(null);
    setCycledDriverData(null);
  }, [pageDate]);

  // Fetch driver data when viewing a different date
  useEffect(() => {
    if (!viewingDate || viewingDate === pageDate) {
      setCycledDriverData(null);
      return;
    }

    let cancelled = false;
    setCycleLoading(true);

    api.get(`/driver-status-monitor/drivers/${driver.unitnumber}?start_date=${viewingDate}&end_date=${viewingDate}`)
      .then(response => {
        if (!cancelled) {
          setCycledDriverData(response.data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCycledDriverData({
            ...driver,
            moves: [],
            ghost_suggestions: [],
          });
        }
      })
      .finally(() => {
        if (!cancelled) setCycleLoading(false);
      });

    return () => { cancelled = true; };
  }, [viewingDate, pageDate, driver.unitnumber]);

  // Effective driver data (cycled or original)
  const effectiveDate = viewingDate && viewingDate !== pageDate ? viewingDate : null;
  const effectiveDriver = effectiveDate && cycledDriverData
    ? { ...driver, moves: cycledDriverData.moves, ghost_suggestions: cycledDriverData.ghost_suggestions }
    : driver;
  const todayDateStr = new Date().toISOString().split('T')[0];
  const isPastDay = effectiveDate ? effectiveDate < todayDateStr : false;

  // Day cycling handlers
  const handleDayNav = useCallback((direction: -1 | 1) => {
    const currentDate = viewingDate || pageDate || new Date().toISOString().split('T')[0];
    const d = new Date(currentDate + 'T12:00:00');
    d.setDate(d.getDate() + direction);
    const newDate = d.toISOString().split('T')[0];
    setViewingDate(newDate);
  }, [viewingDate, pageDate]);

  const handleDateReset = useCallback(() => {
    setViewingDate(null);
    setCycledDriverData(null);
  }, []);

  // Format viewing date for display
  const viewingDateLabel = useMemo(() => {
    if (!effectiveDate) return null;
    const d = new Date(effectiveDate + 'T12:00:00');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    return `${mm}/${dd}/${yy}`;
  }, [effectiveDate]);

  // Nullify interaction callbacks for past days
  const effectiveOnRemove = isPastDay ? undefined : onRemove;
  const effectiveOnQuickAssign = isPastDay ? undefined : onQuickAssign;
  const effectiveOnDragStart = isPastDay ? undefined : onDragStart;
  const effectiveOnDragEnd = isPastDay ? undefined : onDragEnd;
  const effectiveOnDragOver = isPastDay ? undefined : onDragOver;
  const effectiveOnDrop = isPastDay ? undefined : onDrop;
  const effectiveOnReorder = isPastDay ? undefined : onReorder;
  const effectiveOpenAvailableMovesDrawer = isPastDay ? undefined : openAvailableMovesDrawer;
  const effectiveOnGhostCardClick = isPastDay ? undefined : onGhostCardClick;
  const effectiveOnOpenDeadheadMap = isPastDay ? undefined : onOpenDeadheadMap;

  // Get current move (first move that's actually in progress - not just dispatched)
  const currentMove = effectiveDriver.moves.find(m =>
    m.status === 'In Transit' || m.status === 'At Origin' || m.status === 'At Destination' || m.status === 'Started'
  ) || null;

  // Driver hasn't started for the day if ALL moves are still Dispatched (Not Started).
  // Don't show ETAs on any moves since we don't know where they are or if they will work.
  const driverHasStarted = effectiveDriver.moves.some(m => m.status !== 'Dispatched');

  // Calculate total estimated travel time (move time + deadhead between moves)
  // Only include moves due TODAY - tomorrow's moves don't count against today's capacity
  const incompleteMoves = effectiveDriver.moves.filter(m => m.status !== 'Completed');
  const todayStr = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
  const apptChip = nextDeliveryAppt(effectiveDriver, todayStr);
  const APPT_CHIP_TITLE = 'Next delivery appointment (earliest incomplete move)';
  // Samsara start time for the viewed day. A carried-over shift was already
  // running at midnight, so the value is the prior day's start.
  const startTime = formatStartTime(driver.driver_start_at);
  const startChip = startTime
    ? `${startTime}${driver.driver_start_carried_over ? ' (prior day)' : ''}`
    : null;

  // Helper to check if move is due today (or earlier - past due)
  const isMoveToday = (move: MoveRisk): boolean => {
    if (!move.deadline_date_raw) return true; // No date = assume today
    return move.deadline_date_raw <= todayStr;
  };

  const totalEstimatedMinutes = incompleteMoves.reduce((total, move, idx) => {
    // Skip moves due tomorrow or later
    if (!isMoveToday(move)) return total;

    // Add move's estimated time
    let moveTotal = total + (move.estimated_minutes || 0);

    // Add deadhead to next move (if not the last move and next move is also due today)
    if (idx < incompleteMoves.length - 1) {
      const nextMove = incompleteMoves[idx + 1];
      // Only add deadhead if the next move is also due today
      if (isMoveToday(nextMove)) {
        // Check if this move chains to the next (destcode === next origcode means no travel)
        const isChained = move.destcode === nextMove.origcode;

        if (!isChained) {
          // Use travel_to_next_minutes if available, otherwise assume 30 minutes
          const deadhead = move.travel_to_next_minutes ?? 30;
          moveTotal += deadhead;
        }
      }
    }

    return moveTotal;
  }, 0);

  // Check if estimated travel exceeds HOS remaining (over capacity)
  // Skip this check for off-duty drivers
  const isOverCapacity = !driver.off_duty &&
    driver.drive_time_remaining_mins !== null &&
    incompleteMoves.length > 0 &&
    totalEstimatedMinutes > driver.drive_time_remaining_mins;

  // Effective risk level (can be elevated to critical if over capacity, but not if off duty)
  const effectiveRiskLevel = isOverCapacity ? 'critical' : driver.risk_level;

  // HOS Violation: ONLY shows when driver has 0 minutes remaining AND is actively driving/onDuty
  const hasHOSViolation = driver.out_of_hours;

  // Calculate cascading projected status and ETA for each move.
  // Logic lives in the shared, pure `etaProjection` module so a PRO-detail
  // drawer can reuse the exact same math. The module filters 'Completed'
  // internally, so passing raw `effectiveDriver.moves` is equivalent to the
  // previous `incompleteMoves` input.
  // INTENTIONAL: recompute is now keyed on the stable `effectiveDriver.moves`
  // reference (busts on each data refresh) rather than the old fresh-array-
  // every-render `incompleteMoves`. It no longer recomputes on incidental
  // re-renders between polls — that sheds churn; DSM parity holds at poll
  // boundaries (this is by design, not a bug).
  const moveProjectedData = useMemo(
    () => computeMoveProjections(effectiveDriver.moves),
    [effectiveDriver.moves]
  );

  const routeCascade = useMemo(() => computeRouteCascade(effectiveDriver.moves), [effectiveDriver.moves]);

  // Helper to get projected status for a move
  const getProjectedStatus = (move: MoveRisk): ProjectedStatus => {
    return moveProjectedData.get(`${move.pronumber}-${move.legnum}`)?.status || 'on_track';
  };

  // Helper to get projected ETA for a move
  const getProjectedETA = (move: MoveRisk): Date | null => getProjectedETAShared(move, moveProjectedData);

  // Helper to check if ETA is to origin or destination
  const isETAToDestination = (move: MoveRisk): boolean => isETAToDestShared(move, moveProjectedData);

  // Helper to get destination ETA (always returns destination ETA regardless of what's being shown)
  const getDestinationETA = (move: MoveRisk): Date | null => {
    return moveProjectedData.get(`${move.pronumber}-${move.legnum}`)?.etaToDestination || null;
  };

  // Helper to check if move is linked to previous
  const isMoveLinkedToPrev = (move: MoveRisk): boolean => {
    return moveProjectedData.get(`${move.pronumber}-${move.legnum}`)?.isLinkedToPrev || false;
  };

  // Helper to check if move is due tomorrow (not today)
  const isMoveDueTomorrow = (move: MoveRisk): boolean => {
    return moveProjectedData.get(`${move.pronumber}-${move.legnum}`)?.isDueTomorrow || false;
  };

  // Format ETA as time string (e.g., "15:45")
  const formatETA = formatETAShared;

  // Helper to check if a move should show late/missed status
  // Returns true if move has a specific appointment (not just receiving hours)
  const shouldShowLateStatus = (move: MoveRisk): boolean => {
    // Check if appointment_time field is set
    if (move.appointment_time && move.appointment_time.trim() !== '') {
      return true;
    }
    // Check if deadline_time indicates a specific appointment (starts with "Appt:")
    if (move.deadline_time?.startsWith('Appt:')) {
      return true;
    }
    // For receiving hours, only show late status if deadline is after 5 PM
    const timeMatch = move.deadline_time?.match(/(\d{1,2}):(\d{2})/);
    if (timeMatch) {
      const hour = parseInt(timeMatch[1], 10);
      return hour >= 17;
    }
    return false;
  };

  // Helper to check if "At Destination" move actually arrived late
  const didArriveLateFn = (move: MoveRisk): boolean => {
    if (move.status !== 'At Destination') return false;
    if (!move.dest_arrival_time) return false;

    const deadlineTimeMatch = move.deadline_time?.match(/(\d{1,2}):(\d{2})/);
    if (!deadlineTimeMatch) return false;

    const deadlineHour = parseInt(deadlineTimeMatch[1], 10);
    const deadlineMinute = parseInt(deadlineTimeMatch[2], 10);
    const deadlineMinutes = deadlineHour * 60 + deadlineMinute;

    const arrivalMatch = move.dest_arrival_time.match(/(\d{1,2}):(\d{2})/);
    if (!arrivalMatch) return false;

    const arrHour = parseInt(arrivalMatch[1], 10);
    const arrMinute = parseInt(arrivalMatch[2], 10);
    const arrivalMinutes = arrHour * 60 + arrMinute;

    return arrivalMinutes > deadlineMinutes;
  };

  // Check for missed or late moves based on cascading calculations
  const hasMissedMove = incompleteMoves.some(m => {
    if (!shouldShowLateStatus(m)) return false;
    if (m.status === 'At Destination') {
      return didArriveLateFn(m);
    }
    return getProjectedStatus(m) === 'missed';
  });
  const hasLateMove = !hasMissedMove && incompleteMoves.some(m => {
    if (!shouldShowLateStatus(m)) return false;
    if (m.status === 'At Destination') return false;
    return getProjectedStatus(m) === 'will_miss';
  });

  // Will Miss: any move projected to miss that isn't already caught by Late/Missed (non-appointment moves)
  const hasWillMiss = !hasMissedMove && !hasLateMove && incompleteMoves.some(m => {
    if (shouldShowLateStatus(m)) return false; // Already covered by Late/Missed badges
    const status = getProjectedStatus(m);
    return status === 'will_miss' || status === 'missed';
  });

  // Reorder moves: completed first (if shown), then active, then not started
  const displayGroups = useMemo(() => {
    const getStatusPriority = (status: string): number => {
      switch (status) {
        case 'Completed': return 0;
        case 'In Transit': return 1;
        case 'At Origin': return 2;
        case 'At Destination': return 3;
        case 'Started': return 4;
        case 'Dispatched': return 5;
        default: return 6;
      }
    };

    // First, sort all moves by PRO and legnum to ensure proper grouping
    const sortedForGrouping = [...effectiveDriver.moves].sort((a, b) => {
      if (a.pronumber !== b.pronumber) return a.pronumber - b.pronumber;
      return a.legnum - b.legnum;
    });

    // Group consecutive legs on same PRO FIRST (before status sorting)
    const groups = groupConsecutiveLegs(sortedForGrouping);

    // For each group, determine its "lead" move (most active status) for sorting
    const groupsWithSortInfo = groups.map(group => {
      const leadMove = group.moves.reduce((lead, move) => {
        return getStatusPriority(move.status) < getStatusPriority(lead.status) ? move : lead;
      }, group.moves[0]);

      const isCompleted = group.moves.every(m => m.status === 'Completed');
      const hasActive = group.moves.some(m =>
        m.status === 'In Transit' || m.status === 'At Origin' || m.status === 'At Destination' || m.status === 'Started'
      );
      const hasMissedAppt = group.moves.some(m => m.appt_missed === true);

      return {
        group,
        leadMove,
        isCompleted,
        hasActive,
        hasMissedAppt,
        sortPriority: isCompleted
          ? (leadMove.completion_order ?? 999)
          : (leadMove.dispatch_priority && leadMove.dispatch_priority > 0)
            ? leadMove.dispatch_priority
            : 999,
      };
    });

    // Show completed if toggle is on OR if all moves are completed
    const hasNonCompleted = groupsWithSortInfo.some(g => !g.isCompleted);
    const allMovesCompleted = !hasNonCompleted && groupsWithSortInfo.length > 0;
    const shouldShowCompleted = showCompleted || allMovesCompleted;

    // Filter and sort groups
    const filteredGroups = shouldShowCompleted
      ? groupsWithSortInfo
      : groupsWithSortInfo.filter(g => !g.isCompleted);

    filteredGroups.sort((a, b) => {
      // Missed-appointment groups (red cards) bubble to the very top
      if (a.hasMissedAppt && !b.hasMissedAppt) return -1;
      if (!a.hasMissedAppt && b.hasMissedAppt) return 1;

      if (a.isCompleted && !b.isCompleted) return -1;
      if (!a.isCompleted && b.isCompleted) return 1;

      if (a.hasActive && !b.hasActive) return -1;
      if (!a.hasActive && b.hasActive) return 1;

      if (a.sortPriority !== b.sortPriority) return a.sortPriority - b.sortPriority;

      if (a.leadMove.pronumber !== b.leadMove.pronumber) return a.leadMove.pronumber - b.leadMove.pronumber;
      return a.leadMove.legnum - b.leadMove.legnum;
    });

    return filteredGroups.map(g => g.group);
  }, [effectiveDriver.moves, showCompleted]);

  // Get not-started groups only (for reordering)
  const notStartedGroups = useMemo(() => {
    return displayGroups.filter(g => g.moves[0]?.status === 'Dispatched');
  }, [displayGroups]);

  // Flat list for calculating visual priorities
  const flatDisplayMoves = useMemo(() => {
    return displayGroups.flatMap(g => g.moves);
  }, [displayGroups]);

  // Check if we're dragging within this same driver
  const isDraggingWithinSameDriver = dragState && dragState.sourceDriver === driver.unitnumber;

  // Handle drop for reordering within same driver
  const handleReorderDrop = useCallback((targetGroupIndex: number) => {
    if (!dragState || !isDraggingWithinSameDriver || !effectiveOnReorder) return;

    const draggedGroup = dragState.group || notStartedGroups.find(g =>
      g.moves.some(m => m.pronumber === dragState.move.pronumber && m.legnum === dragState.move.legnum)
    );
    if (!draggedGroup) return;

    const currentIndex = notStartedGroups.findIndex(g => g.id === draggedGroup.id);
    if (currentIndex === -1 || currentIndex === targetGroupIndex) {
      setDropTargetIndex(null);
      return;
    }

    const newOrder = [...notStartedGroups];
    newOrder.splice(currentIndex, 1);
    newOrder.splice(targetGroupIndex > currentIndex ? targetGroupIndex - 1 : targetGroupIndex, 0, draggedGroup);

    effectiveOnReorder(driver.unitnumber, newOrder);
    setDropTargetIndex(null);
  }, [dragState, isDraggingWithinSameDriver, notStartedGroups, effectiveOnReorder, driver.unitnumber]);

  // Reset drop target when drag ends
  useEffect(() => {
    if (!dragState) {
      setDropTargetIndex(null);
    }
  }, [dragState]);

  // Helper to get ghost suggestions for a specific position
  const getGhostsForPosition = useCallback((position: 'before_first' | 'between' | 'after_last', beforePro?: number, beforeLeg?: number, afterPro?: number, afterLeg?: number): GhostSuggestion[] => {
    if (!effectiveDriver.ghost_suggestions) return [];
    const ghostCardCount = displaySettings.other.ghostCardCount || 1;
    return effectiveDriver.ghost_suggestions
      .filter(s => {
        if (s.insertion_type !== position) return false;
        if (position === 'between') {
          return s.before_pronumber === beforePro && s.before_legnum === beforeLeg &&
                 s.after_pronumber === afterPro && s.after_legnum === afterLeg;
        }
        return true;
      })
      .sort((a, b) => a.rank_at_position - b.rank_at_position)
      .slice(0, ghostCardCount);
  }, [effectiveDriver.ghost_suggestions, displaySettings.other.ghostCardCount]);

  // Get risk level class for border color
  const riskLevelClass = effectiveRiskLevel === 'critical'
    ? styles.driverLaneCritical
    : effectiveRiskLevel === 'at_risk'
      ? styles.driverLaneAtRisk
      : styles.driverLaneOnTrack;

  const isCompact = !!displaySettings.cardOptions?.compactView;

  const isSuperCompact = isCompact && !!displaySettings.cardOptions?.superCompactView;

  const superCompactFontSize = displaySettings.cardOptions?.superCompactFontSize ?? 9;

  const isVertical = orientation === 'vertical';

  return (
    <div className={`${styles.driverLane} ${riskLevelClass}${isCompact ? ` ${styles.driverLaneCompact}` : ''}${isSuperCompact ? ` ${styles.driverLaneSuperCompact}` : ''}`} data-unitnumber={driver.unitnumber}>
      {/* Header */}
      {!hideHeader && (
        isSuperCompact ? (
          <div className={styles.superCompactHeader} style={{ fontSize: `${superCompactFontSize}px` }}>
            <div className={styles.superCompactHeaderTop}>
              <span className={styles.superCompactUnit}>{effectiveDriver.unitnumber}<MuleBadge isMule={effectiveDriver.isMule} /></span>
              <span className={styles.superCompactName}>{effectiveDriver.driver_name}</span>
              {effectiveDriver.preference && (
                <span className={`${styles.superCompactPref} ${
                  effectiveDriver.preference === 'LOCAL' ? styles.superCompactPrefLocal :
                  effectiveDriver.preference === 'OTR' ? styles.superCompactPrefOTR : ''
                }`}>
                  {effectiveDriver.preference === 'LOCAL' ? 'L' : effectiveDriver.preference === 'OTR' ? 'O' : effectiveDriver.preference[0]}
                </span>
              )}
            </div>
            {(hasDispatchRequest || effectiveDriver.missing_hos || (isToday && effectiveDriver.out_of_service) || (isToday && effectiveDriver.stale_gps)) && (
              <div className={styles.superCompactHeaderIcons}>
                {hasDispatchRequest && (
                  <Tooltip placement="bottom" content="Driver requested more work">
                    <HandRaisedIcon className={styles.superCompactIconSmall} />
                  </Tooltip>
                )}
                {effectiveDriver.missing_hos && (
                  <Tooltip placement="bottom" content="Missing HOS">
                    <ExclamationTriangleIcon className={styles.superCompactIconSmall} />
                  </Tooltip>
                )}
                {isToday && effectiveDriver.out_of_service && (
                  <Tooltip placement="bottom" content={effectiveDriver.out_of_service_reason ?? 'Out of Service'}>
                    <XMarkIcon className={styles.superCompactIconSmall} />
                  </Tooltip>
                )}
                {isToday && effectiveDriver.stale_gps && (
                  <Tooltip placement="bottom" content="Stale GPS">
                    <MapIcon className={styles.superCompactIconSmall} />
                  </Tooltip>
                )}
              </div>
            )}
          </div>
        ) : (
        <div className={`${styles.driverLaneHeader}${isCompact ? ` ${styles.driverLaneHeaderCompact}` : ''}`}>
        {/* Left side: name, endorsements, status, alerts */}
        <div className={styles.driverLaneHeaderLeft}>
          {/* Row 1: Unit number, name, preference, icons */}
          <div className={styles.driverName}>
            {hasDispatchRequest && (
              <Tooltip placement="bottom" content="Driver requested more work">
                <HandRaisedIcon className={styles.dispatchRequestIcon} />
              </Tooltip>
            )}
            <Tooltip placement="bottom" content="View in CMS">
              <a
                href={`${config.V2_URL}/safety/drivers/${parseInt(driver.unitnumber, 10)}/driver`}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.unitNumberLink}
                onClick={(e) => e.stopPropagation()}
              >
                {driver.unitnumber}<MuleBadge isMule={driver.isMule} />
              </a>
            </Tooltip>
            <span className={styles.driverUnitnumber}> - {driver.driver_name}</span>
            {driver.driver_dispatcher && (
              <span className={styles.driverUnitnumber}>[{driver.driver_dispatcher}]</span>
            )}
            {driver.truck_license_plate && (
              <span className="text-xs text-gray-500 ml-1">- {driver.truck_license_plate}</span>
            )}
            {driver.preference && (
              <span className={`text-xs font-medium ml-1 ${driver.preference === 'LOCAL' ? 'text-teal-700' : driver.preference === 'OTR' ? 'text-purple-700' : 'text-gray-500'}`}>
                - {driver.preference}
              </span>
            )}
            <span className={styles.driverIcons}>
              {(() => {
                const note = driver.today_comment?.trim() || null;
                const icon = (
                  <ChatBubbleBottomCenterTextIcon
                    data-testid="driver-note-icon"
                    className={`${styles.commentIcon} ${note ? styles.commentIconActive : styles.commentIconEmpty}`}
                  />
                );
                return (
                  <Tooltip placement="bottom" content={
                    <div>
                      <div>{note || 'No Comment'}</div>
                      {onEditComment && <div className={styles.commentEditHint}>Click to {note ? 'edit' : 'add a note'}</div>}
                    </div>
                  }>
                    {onEditComment ? (
                      <button
                        type="button"
                        className={styles.commentButton}
                        aria-label={`${note ? 'Edit' : 'Add'} driver note for ${driver.unitnumber}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditComment(driver.unitnumber, driver.driver_name, note);
                        }}
                      >
                        {icon}
                      </button>
                    ) : icon}
                  </Tooltip>
                );
              })()}
              {driver.samsara_driver_id && (
                <Tooltip placement="bottom" content="View in Samsara">
                  <a
                    href={`https://cloud.samsara.com/o/39743/fleet/reports/hos/driver/${driver.samsara_driver_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.samsaraLink}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <svg viewBox="0 0 500 500" className={styles.samsaraIcon}>
                      <path fill="#00263e" d="M282.14,50.71c-9.23,0-18.63,1.55-27.92,4.66-4.24,1.43-8.84,1.4-13.08,0-9.29-3.13-18.69-4.71-27.92-4.71-53.01-.04-97.82,53.13-97.86,116.08-.04,70.08,53.31,131.99,129.76,150.55,1.55.37,3.21.37,4.75,0,76.48-18.46,129.93-80.29,129.98-150.37.04-62.95-44.7-116.16-97.71-116.21ZM343.65,160.24c-4.17,9.42-10.58,16.89-19.02,22.2-7.46,4.69-15.77,7.23-24.7,7.54-3.66.09-6.99-2.85-7.12-6.67-.13-3.82,2.84-7.02,6.65-7.15,6.48-.22,12.48-2.05,17.83-5.42,5.73-3.62,10.17-8.64,13.22-14.93,1.53-3.16,2.05-8.53.66-11.75-2.08-4.82-6.34-7.83-11.34-8.13-9.29-.54-16.97,2.27-23.02,8.31-9.79,9.8-13.79,27.32-15.49,34.79-1.46,6.41-17.77,75.74-24.37,103.75-1.2,5.1-6.2,8.44-11.64,7.25-3.72-.81-6.46-4.02-7.33-7.73l-2.26-9.68c-7.44-31.83-20.54-87.92-21.86-93.6-1.72-7.54-5.7-25.03-15.47-34.86-6.04-6.07-13.79-8.88-23.02-8.36-6.37.35-11.62,5.2-12.52,12.55-.2,1.66.13,3.38.76,4.93,3.01,7.44,7.82,13.27,14.26,17.33,5.36,3.39,11.36,5.23,17.83,5.44,3.81.13,6.78,3.35,6.65,7.15-.13,3.8-3.4,6.78-7.12,6.67-8.93-.32-17.23-2.85-24.7-7.56-9.07-5.74-15.78-13.95-19.9-24.39-.81-2.05-1.36-4.24-1.47-6.44-.83-16.63,11.29-28.68,25.42-29.48,13.19-.78,24.74,3.54,33.54,12.4,12.56,12.63,17.17,32.87,19.12,41.51,1.23,5.33,12.67,54.38,20.24,86.77,7.62-32.39,19.15-81.4,20.35-86.71,1.98-8.68,6.6-28.9,19.19-41.53,8.82-8.83,20.42-13.13,33.56-12.33,8.78.52,16.78,5.36,21.38,12.94,4.63,7.64,5.25,17.06,1.61,25.18h.06Z"/>
                    </svg>
                  </a>
                </Tooltip>
              )}
              {driver.driver_lat && driver.driver_lng && (
                <>
                  <Tooltip placement="bottom" content="View Driver Location">
                    <span
                      className={styles.liveMapLink}
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowDriverLocationMap(true);
                      }}
                    >
                      <MapIcon className={styles.liveMapIcon} />
                    </span>
                  </Tooltip>
                  {showDriverLocationMap && createPortal(
                    <div className={styles.locationMapOverlay} onClick={() => setShowDriverLocationMap(false)}>
                      <div
                        className={styles.locationMapPopup}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className={styles.locationMapHeader}>
                          <span className={styles.locationMapTitle}>
                            {driver.unitnumber}<MuleBadge isMule={driver.isMule} /> - {driver.driver_name}
                            {currentMove && ` - ${currentMove.origin_name} → ${currentMove.dest_name} - ${currentMove.pronumber}-${currentMove.legnum}`}
                          </span>
                          <button
                            className={styles.locationMapCloseBtn}
                            onClick={() => setShowDriverLocationMap(false)}
                            title="Close"
                          >
                            <XMarkIcon className={styles.locationMapActionIcon} />
                          </button>
                        </div>
                        <div className={styles.locationMapContent}>
                          {renderLocationMap ? renderLocationMap({
                            driverLat: driver.driver_lat!,
                            driverLng: driver.driver_lng!,
                            originLat: currentMove?.origin_lat,
                            originLng: currentMove?.origin_lng,
                            destLat: currentMove?.dest_lat,
                            destLng: currentMove?.dest_lng,
                          }) : (
                            <div style={{ width: 600, height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', background: document.documentElement.classList.contains('dark') ? '#374151' : '#f3f4f6' }}>
                              Map not available
                            </div>
                          )}
                        </div>
                      </div>
                    </div>,
                    document.body
                  )}
                </>
              )}
              <DriverPhoneLink
                textmsgnumber={driver.textmsgnumber}
                homephone={driver.homephone}
                size={16}
              />
              {viewingDateLabel && (
                <span
                  className={styles.viewingDateBadge}
                  onClick={(e) => { e.stopPropagation(); handleDateReset(); }}
                  title="Click to reset to page date"
                >
                  {viewingDateLabel}
                </span>
              )}
            </span>
          </div>
          {/* Row 2: Request info (only when requesting work) + badges (endorsements, status, alerts) */}
          <div className={styles.driverBadgesRow}>
            {hasDispatchRequest && driver.request_createdatetime && (
              <Tooltip
                placement="bottom"
                content={new Date(driver.request_createdatetime).toLocaleString('en-US', {
                  timeZone: 'America/Chicago',
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  timeZoneName: 'short',
                })}
              >
                <span className={styles.requestAge}>
                  Requested {formatRequestAge(driver.request_createdatetime)}
                </span>
              </Tooltip>
            )}
            {hasDispatchRequest && driver.request_createdatetime && driver.formatted_location && (
              <span className={styles.requestInfoSep}>·</span>
            )}
            {hasDispatchRequest && driver.formatted_location && (
              <span className={styles.requestLocation} title={driver.formatted_location}>
                {driver.formatted_location}
              </span>
            )}
            {hasDispatchRequest && isToday && onAcknowledgeRequest && (
              <Tooltip placement="bottom" content="Acknowledge and clear the request">
                <button
                  type="button"
                  className={styles.ackRequestButton}
                  aria-label={`Acknowledge work request for ${driver.unitnumber}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onAcknowledgeRequest(driver.unitnumber);
                  }}
                >
                  Acknowledge Request
                </button>
              </Tooltip>
            )}
            {/* Qualification badges (endorsements) */}
            {displaySettings.other.endorsements && (
              <>
                {driver.has_hazmat && <span className={`${styles.qualBadge} ${styles.qualBadgeHazmat}`}>HAZ</span>}
                {driver.has_tank && <span className={`${styles.qualBadge} ${styles.qualBadgeTank}`}>TANK</span>}
                {driver.has_93k && <span className={`${styles.qualBadge} ${styles.qualBadge93k}`}>93K</span>}
                {driver.is_daycab && <span className={`${styles.qualBadge} ${styles.qualBadgeDaycab}`}>DAY</span>}
              </>
            )}
            {/* Status badges */}
            {currentMove && displaySettings.statusTypes.currentMove && (
              <span className={styles.currentMoveBadge}>
                Current Move: {currentMove.pronumber}-{currentMove.legnum}
              </span>
            )}
            {!currentMove && effectiveDriver.moves.length > 0 && (
              effectiveDriver.moves.some(m => m.status === 'Completed') ? (
                displaySettings.statusTypes.betweenMoves && (
                  <span className={styles.betweenMovesBadge}>
                    <ExclamationTriangleIcon className="w-3 h-3" />
                    Between Moves
                  </span>
                )
              ) : (
                displaySettings.statusTypes.notStarted && (
                  <span className={styles.notStartedBadge}>
                    <ExclamationTriangleIcon className="w-3 h-3" />
                    Not Started
                  </span>
                )
              )
            )}
            {/* Missed badge */}
            {hasMissedMove && (
              <span className={styles.missedBadge}>
                <ExclamationTriangleIcon className="w-3 h-3" />
                Missed
              </span>
            )}
            {/* Late badge */}
            {hasLateMove && (
              <span className={styles.lateBadge}>
                <ExclamationTriangleIcon className="w-3 h-3" />
                Late
              </span>
            )}
            {/* HOS Violation badge */}
            {hasHOSViolation && (
              <span className={styles.hosViolationBadge}>
                <ExclamationTriangleIcon className="w-3 h-3" />
                HOS Violation
              </span>
            )}
            {isToday && driver.out_of_service ? (
              <span
                className={styles.outOfServiceBadge}
                title={
                  driver.out_of_service_reason
                    ? `Out of Service: ${driver.out_of_service_reason}${driver.out_of_service_date ? ` (since ${driver.out_of_service_date})` : ''}`
                    : 'Driver/unit is out of service'
                }
              >
                Out Of Service
              </span>
            ) : (
              isToday && driver.missing_hos && <span className={styles.warningBadge} title="Missing HOS data">No HOS</span>
            )}
            {isToday && driver.stale_gps && (
              driver.vin_mismatch_issue ? (
                <Tooltip placement="bottom" content={
                  <div className={styles.vinMismatchTooltip}>
                    <div className={styles.vinMismatchTooltipTitle}>Stale GPS - Data Issue Detected</div>
                    <div className={styles.vinMismatchTooltipIssue}>{driver.vin_mismatch_issue}</div>
                    <div className={styles.vinMismatchTooltipHint}>Check Driver Data Issues page for details</div>
                  </div>
                }>
                  <span className={`${styles.warningBadge} ${styles.warningBadgeVinMismatch}`}>Stale GPS</span>
                </Tooltip>
              ) : (
                <span className={styles.warningBadge} title="GPS data is stale">Stale GPS</span>
              )
            )}
            {/* Not Enough Hours badge - estimated travel exceeds HOS remaining */}
            {isToday && isOverCapacity && !hasHOSViolation && (
              <Tooltip placement="bottom" content={
                `Est. travel ${formatMinutes(totalEstimatedMinutes)} exceeds ${formatMinutes(driver.drive_time_remaining_mins ?? 0)} HOS remaining`
              }>
                <span className={styles.notEnoughHoursBadge}>
                  <ExclamationTriangleIcon className="w-3 h-3" />
                  Not Enough Hours
                </span>
              </Tooltip>
            )}
            {/* Will Miss badge - projected to miss non-appointment deadlines */}
            {hasWillMiss && (
              <span className={styles.willMissBadge}>
                <ExclamationTriangleIcon className="w-3 h-3" />
                Will Miss
              </span>
            )}
            {/* Off Duty badge */}
            {isToday && driver.off_duty && incompleteMoves.length > 0 && (
              <span className={styles.offDutyBadge}>Off Duty</span>
            )}
            {/* Limiting factor badge */}
            {isToday && !driver.off_duty && incompleteMoves.length > 0 && (effectiveRiskLevel === 'critical' || effectiveRiskLevel === 'at_risk') && (
              driver.limiting_factor === 'hos' ? (
                <span className={styles.hosLimitedBadge} title="Bottleneck is HOS drive time remaining">HOS Limited</span>
              ) : (
                <span className={styles.timeLimitedBadge} title="Bottleneck is time remaining in the day">Time Limited</span>
              )
            )}
          </div>
        </div>

        {/* Right side: stats and HOS widget */}
        {isCompact ? (
          <div className={styles.driverLaneHeaderRightCompact}>
            <span className={styles.driverStatCompact}>
              {effectiveDriver.moves.filter(m => m.status === 'Completed').length} comp, {effectiveDriver.moves.filter(m => m.status !== 'Completed').length} rem
            </span>
            {startChip && (
              <>
                <span className={styles.compactSep}>·</span>
                <span className={styles.driverStatCompact} title={START_CHIP_TITLE}>
                  Start <span className={styles.driverStatValue}>{startChip}</span>
                </span>
              </>
            )}
            {apptChip && (
              <>
                <span className={styles.compactSep}>·</span>
                <span className={styles.driverStatCompact} title={APPT_CHIP_TITLE}>
                  Appt <span className={styles.driverStatValue}>{apptChip.display}</span>
                </span>
              </>
            )}
            {isToday && incompleteMoves.length > 0 && (
              <>
                <span className={styles.compactSep}>·</span>
                <span className={styles.driverStatCompact}>
                  Est: <span className={styles.driverStatValue}>{formatMinutesCompact(totalEstimatedMinutes)}</span>
                </span>
              </>
            )}
            {isToday && (
              <>
                <span className={styles.compactSep}>·</span>
                <CompactHosWidget
                  currentDutyStatus={driver.current_duty_status}
                  breakTimeRemainingMins={driver.break_time_remaining_mins}
                  driveTimeRemainingMins={driver.drive_time_remaining_mins}
                  shiftTimeRemainingMins={driver.shift_time_remaining_mins}
                  cycleTimeRemainingMins={driver.cycle_time_remaining_mins}
                  samsaraDriverId={driver.samsara_driver_id}
                  inline
                />
              </>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <div className="flex flex-col items-end text-right">
              <span className={styles.driverStat}>
                {effectiveDriver.moves.filter(m => m.status === 'Completed').length} complete, {effectiveDriver.moves.filter(m => m.status !== 'Completed').length} remaining
              </span>
              {startChip && (
                <span className={styles.driverStat} title={START_CHIP_TITLE}>
                  {'Start: '}
                  <span className={styles.driverStatValue}>{startChip}</span>
                </span>
              )}
              {apptChip && (
                <span className={styles.driverStat} title={APPT_CHIP_TITLE}>
                  {'Next Appt: '}
                  <span className={styles.driverStatValue}>{apptChip.display}</span>
                </span>
              )}
              {isToday && incompleteMoves.length > 0 && (
                <span className={styles.driverStat}>
                  {'Est. Travel: '}
                  <span className={styles.driverStatValue}>
                    {formatMinutes(totalEstimatedMinutes)}
                  </span>
                </span>
              )}
            </div>
            {isToday && (
              <CompactHosWidget
                currentDutyStatus={driver.current_duty_status}
                breakTimeRemainingMins={driver.break_time_remaining_mins}
                driveTimeRemainingMins={driver.drive_time_remaining_mins}
                shiftTimeRemainingMins={driver.shift_time_remaining_mins}
                cycleTimeRemainingMins={driver.cycle_time_remaining_mins}
                samsaraDriverId={driver.samsara_driver_id}
              />
            )}
          </div>
        )}
      </div>
        )
      )}

      {/* Day cycling wrapper — wraps only move cards area */}
      <div className={styles.dayCyclingWrapper}>
        {/* Left nav bar (previous day) */}
        <div
          className={styles.dayNavBar}
          onClick={(e) => { e.stopPropagation(); handleDayNav(-1); }}
          title="Previous day"
        >
          <ChevronLeftIcon className={styles.dayNavBarIcon} />
        </div>

        {/* Lane content (move cards + reorder bar) */}
        <div className={styles.dayNavLaneContent}>
      {/* Move Cards */}
      <div
        className={`${isVertical ? styles.moveCardsContainerVertical : styles.moveCardsContainer}${isPastDay ? ` ${styles.pastDayContent}` : ''}`}
        onDragOver={(e) => {
          effectiveOnDragOver?.(e);
          if (isDraggingWithinSameDriver && notStartedGroups.length > 0) {
            setDropTargetIndex(notStartedGroups.length);
          }
        }}
        onDragLeave={() => setDropTargetIndex(null)}
        onDrop={(e) => {
          if (isDraggingWithinSameDriver && dropTargetIndex !== null) {
            e.preventDefault();
            e.stopPropagation();
            handleReorderDrop(dropTargetIndex);
          } else if (effectiveOnDrop) {
            effectiveOnDrop(e, driver.unitnumber);
          }
        }}
      >
        {cycleLoading ? (
          <div className={styles.dayCyclingLoading}>Loading...</div>
        ) : effectiveDate && effectiveDriver.moves.length === 0 ? (
          <div className={styles.emptyDayState}>No moves for {viewingDateLabel}</div>
        ) : !effectiveDate && effectiveDriver.moves.length === 0 ? (
          <div className={isVertical ? styles.moveCardsWrapperVertical : styles.moveCardsWrapper}>
            <div className={styles.singleMoveWrapper}>
              <button
                type="button"
                className={styles.addMoveCard}
                onClick={(e) => {
                  e.stopPropagation();
                  if (effectiveOpenAvailableMovesDrawer) {
                    const mapData = effectiveDriver.driver_lat != null && effectiveDriver.driver_lng != null ? {
                      fromName: 'Driver Location',
                      fromCityState: '',
                      fromLat: effectiveDriver.driver_lat,
                      fromLng: effectiveDriver.driver_lng,
                      toName: 'Driver Location',
                      toCityState: '',
                      toLat: effectiveDriver.driver_lat,
                      toLng: effectiveDriver.driver_lng,
                      travelMinutes: 0,
                    } : undefined;
                    effectiveOpenAvailableMovesDrawer(driver.unitnumber, driver.driver_name, mapData);
                  }
                }}
              >
                <PlusCircleIcon className={styles.addMoveCardIcon} />
                <span className={styles.addMoveCardText}>Add Available Move</span>
              </button>
            </div>
          </div>
        ) : (
          <div className={isVertical ? styles.moveCardsWrapperVertical : styles.moveCardsWrapper}>
            {/* Add Available Move pill at the start - only show if first move is NOT STARTED */}
            {effectiveOpenAvailableMovesDrawer && displayGroups.length > 0 && displayGroups[0].moves[0]?.status === 'Dispatched' && (() => {
              const firstMove = displayGroups[0].moves[0];
              const mapData = firstMove.origin_lat && firstMove.origin_lng ? {
                fromName: firstMove.origin_name,
                fromCityState: firstMove.origin_city_state,
                fromLat: firstMove.origin_lat,
                fromLng: firstMove.origin_lng,
                toName: firstMove.origin_name,
                toCityState: firstMove.origin_city_state,
                toLat: firstMove.origin_lat,
                toLng: firstMove.origin_lng,
                travelMinutes: 0,
              } : undefined;
              return (
                <div
                  className={styles.addMovePillContainer}
                  onClick={(e) => {
                    e.stopPropagation();
                    effectiveOpenAvailableMovesDrawer(driver.unitnumber, driver.driver_name, mapData);
                  }}
                >
                  <div className={styles.addMovePill}>
                    {isCompact ? (
                      <PlusCircleIcon className={styles.addMovePillIconCompact} />
                    ) : (
                      <span className={styles.addMovePillText}>Add Available Move</span>
                    )}
                  </div>
                </div>
              );
            })()}
            {/* Ghost cards for before_first position */}
            {showGhostCards && effectiveOnGhostCardClick && getGhostsForPosition('before_first').map((ghost) => (
              <GhostMoveCard
                key={`ghost-${ghost.id}`}
                suggestion={ghost}
                onClick={effectiveOnGhostCardClick}
              />
            ))}
            {displayGroups.map((group, groupDisplayIndex) => {
              // Calculate visual priority for not-started moves
              const notStartedMoves = flatDisplayMoves.filter(m => m.status === 'Dispatched');
              const isNotStartedGroup = group.moves[0]?.status === 'Dispatched';
              const notStartedGroupIndex = isNotStartedGroup
                ? notStartedGroups.findIndex(g => g.id === group.id)
                : -1;

              // Check if this is the dragged group
              const isDraggedGroup = dragState && (
                dragState.group?.id === group.id ||
                group.moves.some(m => m.pronumber === dragState.move.pronumber && m.legnum === dragState.move.legnum)
              );

              // Get travel time from previous group's last move to this group's first move
              const prevGroup = groupDisplayIndex > 0 ? displayGroups[groupDisplayIndex - 1] : null;
              const prevLastMove = prevGroup ? prevGroup.moves[prevGroup.moves.length - 1] : null;
              const currentFirstMove = group.moves[0];
              const isSameLocation = prevLastMove && currentFirstMove && prevLastMove.destcode === currentFirstMove.origcode;
              const travelTimeFromPrev = prevLastMove?.travel_to_next_minutes;
              const isTravelTimeEstimated = prevLastMove && travelTimeFromPrev == null && !isSameLocation;

              if (group.isGroup) {
                // Render grouped moves with chain link icon between them
                return (
                  <React.Fragment key={group.id}>
                    {/* Add Available Move pill - show if estimated or travel time > 30min */}
                    {effectiveOpenAvailableMovesDrawer && groupDisplayIndex > 0 && prevLastMove &&
                     !isSameLocation && travelTimeFromPrev != null && (travelTimeFromPrev > 30 || isTravelTimeEstimated) &&
                     group.moves[0].status === 'Dispatched' && (
                      <React.Fragment>
                        <div
                          className={styles.addMovePillContainer}
                          onClick={(e) => {
                            e.stopPropagation();
                            const firstMove = group.moves[0];
                            const mapData = prevLastMove.dest_lat && prevLastMove.dest_lng && firstMove.origin_lat && firstMove.origin_lng ? {
                              fromName: prevLastMove.dest_name,
                              fromCityState: prevLastMove.dest_city_state,
                              fromLat: prevLastMove.dest_lat,
                              fromLng: prevLastMove.dest_lng,
                              toName: firstMove.origin_name,
                              toCityState: firstMove.origin_city_state,
                              toLat: firstMove.origin_lat,
                              toLng: firstMove.origin_lng,
                              travelMinutes: travelTimeFromPrev || 30,
                            } : undefined;
                            effectiveOpenAvailableMovesDrawer(driver.unitnumber, driver.driver_name, mapData);
                          }}
                        >
                          <div className={styles.addMovePill}>
                            {isCompact ? (
                      <PlusCircleIcon className={styles.addMovePillIconCompact} />
                    ) : (
                      <span className={styles.addMovePillText}>Add Available Move</span>
                    )}
                          </div>
                          <div className={styles.deadheadIndicator}>
                            <div className={styles.deadheadBracketLeft} />
                            <span className={styles.deadheadTime}>
                              {isTravelTimeEstimated ? '~30min' : formatMinutes(travelTimeFromPrev)}
                            </span>
                            <div className={styles.deadheadBracketRight} />
                          </div>
                        </div>
                      </React.Fragment>
                    )}
                    {/* Travel time from previous group - only show if > 1 min, not estimated, and not showing Add Move */}
                    {groupDisplayIndex > 0 && prevLastMove && !isSameLocation &&
                     travelTimeFromPrev != null && travelTimeFromPrev > 1 && !isTravelTimeEstimated &&
                     travelTimeFromPrev <= 30 && (
                      <div
                        className={styles.travelTimeWrapper}
                        onClick={() => {
                          const firstMoveOfGroup = group.moves[0];
                          if (prevLastMove && firstMoveOfGroup && travelTimeFromPrev != null && effectiveOnOpenDeadheadMap) {
                            effectiveOnOpenDeadheadMap(prevLastMove, firstMoveOfGroup, travelTimeFromPrev, driver.unitnumber, driver.driver_name);
                          }
                        }}
                      >
                        <div className={styles.travelTimeBetweenCards}>
                          {isCompact ? formatMinutesCompact(travelTimeFromPrev) : `Deadhead ${formatMinutes(travelTimeFromPrev)}`}
                        </div>
                      </div>
                    )}
                    {/* Ghost cards for between position - show regardless of travel time */}
                    {showGhostCards && effectiveOnGhostCardClick && groupDisplayIndex > 0 && prevLastMove && !isSameLocation &&
                     group.moves[0].status === 'Dispatched' &&
                     getGhostsForPosition('between', prevLastMove.pronumber, prevLastMove.legnum, currentFirstMove.pronumber, currentFirstMove.legnum).map((ghost) => (
                      <GhostMoveCard
                        key={`ghost-between-${ghost.id}`}
                        suggestion={ghost}
                        onClick={effectiveOnGhostCardClick}
                      />
                    ))}
                    <div className="flex items-stretch">
                      {/* Drop indicator - blue vertical line before this group */}
                      {isNotStartedGroup && isDraggingWithinSameDriver && !isDraggedGroup && dropTargetIndex === notStartedGroupIndex && (
                        <div className={styles.reorderDropIndicator} />
                      )}
                      <div
                        className={`${styles.moveGroupContainer} ${isDraggedGroup ? styles.moveGroupDragging : ''}`}
                        onDragOver={(e) => {
                          if (isNotStartedGroup && isDraggingWithinSameDriver && !isDraggedGroup && notStartedGroupIndex >= 0) {
                            e.preventDefault();
                            e.stopPropagation();
                            setDropTargetIndex(notStartedGroupIndex);
                          }
                        }}
                        onDrop={(e) => {
                          if (isNotStartedGroup && isDraggingWithinSameDriver && !isDraggedGroup && notStartedGroupIndex >= 0) {
                            e.preventDefault();
                            e.stopPropagation();
                            handleReorderDrop(notStartedGroupIndex);
                          }
                        }}
                      >
                      {group.moves.map((move, idx) => {
                        const notStartedIndex = notStartedMoves.findIndex(
                          m => m.pronumber === move.pronumber && m.legnum === move.legnum
                        );
                        const visualPriority = move.status === 'Dispatched' ? notStartedIndex + 1 : undefined;
                        const originalIndex = effectiveDriver.moves.findIndex(
                          m => m.pronumber === move.pronumber && m.legnum === move.legnum
                        );

                        // For linked moves: if current is At Origin and previous is At Destination,
                        // show time at location
                        const prevMove = idx > 0 ? group.moves[idx - 1] : null;
                        const linkedArrivalTime = (
                          move.status === 'At Origin' &&
                          prevMove?.status === 'At Destination' &&
                          prevMove?.dest_arrival_time
                        ) ? prevMove.dest_arrival_time : undefined;

                        return (
                          <React.Fragment key={`${move.pronumber}-${move.legnum}`}>
                            {/* Chain link icon between grouped moves */}
                            {idx > 0 && (
                              <div className={styles.chainLinkWrapper}>
                                <svg
                                  className={styles.chainLinkIcon}
                                  viewBox="0 0 24 24"
                                  fill="currentColor"
                                >
                                  <path d="M17.711,2.021c2.382,.113,4.289,2.069,4.289,4.479,0,2.481-2.019,4.5-4.5,4.5h-4c-2.481,0-4.5-2.019-4.5-4.5,0-.925,.282-1.784,.762-2.5h-2.26c-.322,.77-.502,1.614-.502,2.5,0,0,0,.017,.002,.046,.025,3.563,2.929,6.454,6.498,6.454h4c3.584,0,6.5-2.916,6.5-6.5S21.084,0,17.5,0h-1.534c.684,.576,1.27,1.26,1.745,2.021ZM6.5,13h1.534c-.684-.576-1.27-1.26-1.745-2.021-2.382-.113-4.289-2.069-4.289-4.479,0-2.481,2.019-4.5,4.5-4.5h4c2.481,0,4.5,2.019,4.5,4.5,0,.925-.282,1.784-.762,2.5h2.26c.322-.77,.502-1.614,.502-2.5,0,0,0-.017-.002-.046-.025-3.563-2.929-6.454-6.498-6.454H6.5C2.916,0,0,2.916,0,6.5s2.916,6.5,6.5,6.5Z"/>
                                </svg>
                              </div>
                            )}
                            {isSuperCompact ? (
                              <SuperCompactMoveCard
                                move={move}
                                routeCascade={routeCascade.get(`${move.pronumber}-${move.legnum}`)}
                                projectedStatus={isToday && move.status !== 'Completed' ? getProjectedStatus(move) : undefined}
                                projectedETA={isToday && driverHasStarted && move.status !== 'Completed' ? formatETA(getProjectedETA(move)) : undefined}
                                isETAToDestination={isToday && driverHasStarted && move.status !== 'Completed' ? isETAToDestination(move) : undefined}
                                onProClick={onProClick}
                                group={group}
                                onDragStart={effectiveOnDragStart}
                                onDragEnd={effectiveOnDragEnd}
                                fontSize={superCompactFontSize}
                                showChassis={displaySettings.cardOptions?.showChassis}
                              />
                            ) : displaySettings.cardOptions?.compactView ? (
                              <CompactMoveCard
                                move={move}
                                routeCascade={routeCascade.get(`${move.pronumber}-${move.legnum}`)}
                                projectedStatus={isToday && move.status !== 'Completed' ? getProjectedStatus(move) : undefined}
                                projectedETA={isToday && driverHasStarted && move.status !== 'Completed' ? formatETA(getProjectedETA(move)) : undefined}
                                isETAToDestination={isToday && driverHasStarted && move.status !== 'Completed' ? isETAToDestination(move) : undefined}
                                onProClick={onProClick}
                                group={group}
                                onDragStart={effectiveOnDragStart}
                                onDragEnd={effectiveOnDragEnd}
                                showChassis={displaySettings.cardOptions?.showChassis}
                              />
                            ) : (
                              <MoveCard
                                move={move}
                                routeCascade={routeCascade.get(`${move.pronumber}-${move.legnum}`)}
                                priority={originalIndex + 1}
                                visualPriority={visualPriority}
                                isCurrentMove={currentMove !== null && move.pronumber === currentMove.pronumber && move.legnum === currentMove.legnum}
                                isGroupedDisplay={true}
                                group={group}
                                projectedStatus={isToday && move.status !== 'Completed' ? getProjectedStatus(move) : undefined}
                                projectedETA={isToday && driverHasStarted && move.status !== 'Completed' ? formatETA(getProjectedETA(move)) : undefined}
                                projectedETAToDest={isToday && driverHasStarted && move.status !== 'Completed' && !isETAToDestination(move) ? formatETA(getDestinationETA(move)) : undefined}
                                isETAToDestination={isToday && driverHasStarted && move.status !== 'Completed' ? isETAToDestination(move) : undefined}
                                isLinkedMove={move.status !== 'Completed' ? isMoveLinkedToPrev(move) : undefined}
                                isDueTomorrow={move.status !== 'Completed' ? isMoveDueTomorrow(move) : undefined}
                                linkedArrivalTime={linkedArrivalTime}
                                orientation={orientation}
                                showContainerNumber={displaySettings.cardOptions?.showContainerNumber}
                                showSize={displaySettings.cardOptions?.showSize}
                                showChassis={displaySettings.cardOptions?.showChassis}
                                showAccountStatus={displaySettings.cardOptions?.showAccountStatus}
                                highlightPronumber={highlightPronumber}
                                onProClick={onProClick}
                                onProDetailsClick={onProDetailsClick}
                                onRemove={effectiveOnRemove}
                                onQuickAssign={effectiveOnQuickAssign}
                                onDragStart={effectiveOnDragStart}
                                onDragEnd={effectiveOnDragEnd}
                              />
                            )}
                          </React.Fragment>
                        );
                      })}
                      </div>
                    </div>
                  </React.Fragment>
                );
              } else {
                // Single move
                const move = group.moves[0];
                const notStartedIndex = notStartedMoves.findIndex(
                  m => m.pronumber === move.pronumber && m.legnum === move.legnum
                );
                const visualPriority = move.status === 'Dispatched' ? notStartedIndex + 1 : undefined;
                const originalIndex = effectiveDriver.moves.findIndex(
                  m => m.pronumber === move.pronumber && m.legnum === move.legnum
                );

                return (
                  <React.Fragment key={`${move.pronumber}-${move.legnum}`}>
                    {/* Add Available Move pill between cards */}
                    {effectiveOpenAvailableMovesDrawer && groupDisplayIndex > 0 && prevLastMove &&
                     !isSameLocation && travelTimeFromPrev != null && (travelTimeFromPrev > 30 || isTravelTimeEstimated) &&
                     move.status === 'Dispatched' && (
                      <React.Fragment>
                        <div
                          className={styles.addMovePillContainer}
                          onClick={(e) => {
                            e.stopPropagation();
                            const mapData = prevLastMove.dest_lat && prevLastMove.dest_lng && move.origin_lat && move.origin_lng ? {
                              fromName: prevLastMove.dest_name,
                              fromCityState: prevLastMove.dest_city_state,
                              fromLat: prevLastMove.dest_lat,
                              fromLng: prevLastMove.dest_lng,
                              toName: move.origin_name,
                              toCityState: move.origin_city_state,
                              toLat: move.origin_lat,
                              toLng: move.origin_lng,
                              travelMinutes: travelTimeFromPrev || 30,
                            } : undefined;
                            effectiveOpenAvailableMovesDrawer(driver.unitnumber, driver.driver_name, mapData);
                          }}
                        >
                          <div className={styles.addMovePill}>
                            {isCompact ? (
                      <PlusCircleIcon className={styles.addMovePillIconCompact} />
                    ) : (
                      <span className={styles.addMovePillText}>Add Available Move</span>
                    )}
                          </div>
                          <div className={styles.deadheadIndicator}>
                            <div className={styles.deadheadBracketLeft} />
                            <span className={styles.deadheadTime}>
                              {isSameLocation ? '0min' : isTravelTimeEstimated ? '~30min' : travelTimeFromPrev != null ? formatMinutes(travelTimeFromPrev) : '--'}
                            </span>
                            <div className={styles.deadheadBracketRight} />
                          </div>
                        </div>
                      </React.Fragment>
                    )}
                    {/* Travel time from previous group (when Add Move pill not shown) */}
                    {groupDisplayIndex > 0 && prevLastMove && !isSameLocation &&
                     travelTimeFromPrev != null && travelTimeFromPrev > 1 && !isTravelTimeEstimated &&
                     travelTimeFromPrev <= 30 && (
                      <div
                        className={styles.travelTimeWrapper}
                        onClick={() => {
                          const firstMoveOfGroup = group.moves[0];
                          if (prevLastMove && firstMoveOfGroup && travelTimeFromPrev != null && effectiveOnOpenDeadheadMap) {
                            effectiveOnOpenDeadheadMap(prevLastMove, firstMoveOfGroup, travelTimeFromPrev, driver.unitnumber, driver.driver_name);
                          }
                        }}
                      >
                        <div className={styles.travelTimeBetweenCards}>
                          {isCompact ? formatMinutesCompact(travelTimeFromPrev) : `Deadhead ${formatMinutes(travelTimeFromPrev)}`}
                        </div>
                      </div>
                    )}
                    {/* Ghost cards for between position - show regardless of travel time (single moves) */}
                    {showGhostCards && effectiveOnGhostCardClick && groupDisplayIndex > 0 && prevLastMove && !isSameLocation &&
                     move.status === 'Dispatched' &&
                     getGhostsForPosition('between', prevLastMove.pronumber, prevLastMove.legnum, currentFirstMove.pronumber, currentFirstMove.legnum).map((ghost) => (
                      <GhostMoveCard
                        key={`ghost-between-single-${ghost.id}`}
                        suggestion={ghost}
                        onClick={effectiveOnGhostCardClick}
                      />
                    ))}
                    <div className="flex items-stretch">
                      {/* Drop indicator - blue vertical line before this card */}
                      {isNotStartedGroup && isDraggingWithinSameDriver && !isDraggedGroup && dropTargetIndex === notStartedGroupIndex && (
                        <div className={styles.reorderDropIndicator} />
                      )}
                      <div
                        className={`${styles.singleMoveWrapper} ${isDraggedGroup ? styles.moveCardDragging : ''}`}
                        onDragOver={(e) => {
                          if (isNotStartedGroup && isDraggingWithinSameDriver && !isDraggedGroup && notStartedGroupIndex >= 0) {
                            e.preventDefault();
                            e.stopPropagation();
                            setDropTargetIndex(notStartedGroupIndex);
                          }
                        }}
                        onDrop={(e) => {
                          if (isNotStartedGroup && isDraggingWithinSameDriver && !isDraggedGroup && notStartedGroupIndex >= 0) {
                            e.preventDefault();
                            e.stopPropagation();
                            handleReorderDrop(notStartedGroupIndex);
                          }
                        }}
                      >
                        {isSuperCompact ? (
                          <SuperCompactMoveCard
                            move={move}
                            routeCascade={routeCascade.get(`${move.pronumber}-${move.legnum}`)}
                            projectedStatus={isToday && move.status !== 'Completed' ? getProjectedStatus(move) : undefined}
                            projectedETA={isToday && driverHasStarted && move.status !== 'Completed' ? formatETA(getProjectedETA(move)) : undefined}
                            isETAToDestination={isToday && driverHasStarted && move.status !== 'Completed' ? isETAToDestination(move) : undefined}
                            onProClick={onProClick}
                            group={group}
                            onDragStart={effectiveOnDragStart}
                            onDragEnd={effectiveOnDragEnd}
                            fontSize={superCompactFontSize}
                          />
                        ) : displaySettings.cardOptions?.compactView ? (
                          <CompactMoveCard
                            move={move}
                            routeCascade={routeCascade.get(`${move.pronumber}-${move.legnum}`)}
                            projectedStatus={isToday && move.status !== 'Completed' ? getProjectedStatus(move) : undefined}
                            projectedETA={isToday && driverHasStarted && move.status !== 'Completed' ? formatETA(getProjectedETA(move)) : undefined}
                            isETAToDestination={isToday && driverHasStarted && move.status !== 'Completed' ? isETAToDestination(move) : undefined}
                            onProClick={onProClick}
                            group={group}
                            onDragStart={effectiveOnDragStart}
                            onDragEnd={effectiveOnDragEnd}
                            showChassis={displaySettings.cardOptions?.showChassis}
                          />
                        ) : (
                          <MoveCard
                            move={move}
                            routeCascade={routeCascade.get(`${move.pronumber}-${move.legnum}`)}
                            priority={originalIndex + 1}
                            visualPriority={visualPriority}
                            isCurrentMove={currentMove !== null && move.pronumber === currentMove.pronumber && move.legnum === currentMove.legnum}
                            group={group}
                            projectedStatus={isToday && move.status !== 'Completed' ? getProjectedStatus(move) : undefined}
                            projectedETA={isToday && driverHasStarted && move.status !== 'Completed' ? formatETA(getProjectedETA(move)) : undefined}
                            projectedETAToDest={isToday && driverHasStarted && move.status !== 'Completed' && !isETAToDestination(move) ? formatETA(getDestinationETA(move)) : undefined}
                            isETAToDestination={isToday && driverHasStarted && move.status !== 'Completed' ? isETAToDestination(move) : undefined}
                            isLinkedMove={move.status !== 'Completed' ? isMoveLinkedToPrev(move) : undefined}
                            isDueTomorrow={move.status !== 'Completed' ? isMoveDueTomorrow(move) : undefined}
                            orientation={orientation}
                            showContainerNumber={displaySettings.cardOptions?.showContainerNumber}
                            showSize={displaySettings.cardOptions?.showSize}
                            showAccountStatus={displaySettings.cardOptions?.showAccountStatus}
                            highlightPronumber={highlightPronumber}
                            onProClick={onProClick}
                            onProDetailsClick={onProDetailsClick}
                            onRemove={effectiveOnRemove}
                            onQuickAssign={effectiveOnQuickAssign}
                            onDragStart={effectiveOnDragStart}
                            onDragEnd={effectiveOnDragEnd}
                          />
                        )}
                      </div>
                    </div>
                  </React.Fragment>
                );
              }
            })}
            {/* Drop indicator at the end for not-started groups */}
            {isDraggingWithinSameDriver && notStartedGroups.length > 0 && dropTargetIndex === notStartedGroups.length && (
              <div className={styles.reorderDropIndicator} />
            )}
            {/* Add Available Move pill at the end */}
            {effectiveOpenAvailableMovesDrawer && displayGroups.length > 0 && (() => {
              const lastGroup = displayGroups[displayGroups.length - 1];
              const lastMove = lastGroup.moves[lastGroup.moves.length - 1];
              const mapData = lastMove.dest_lat && lastMove.dest_lng ? {
                fromName: lastMove.dest_name,
                fromCityState: lastMove.dest_city_state,
                fromLat: lastMove.dest_lat,
                fromLng: lastMove.dest_lng,
                toName: lastMove.dest_name,
                toCityState: lastMove.dest_city_state,
                toLat: lastMove.dest_lat,
                toLng: lastMove.dest_lng,
                travelMinutes: 0,
              } : undefined;
              return (
                <div
                  className={styles.addMovePillContainer}
                  onClick={(e) => {
                    e.stopPropagation();
                    effectiveOpenAvailableMovesDrawer(driver.unitnumber, driver.driver_name, mapData);
                  }}
                >
                  <div className={styles.addMovePill}>
                    {isCompact ? (
                      <PlusCircleIcon className={styles.addMovePillIconCompact} />
                    ) : (
                      <span className={styles.addMovePillText}>Add Available Move</span>
                    )}
                  </div>
                </div>
              );
            })()}
            {/* Ghost cards for after_last position */}
            {showGhostCards && effectiveOnGhostCardClick && getGhostsForPosition('after_last').map((ghost) => (
              <GhostMoveCard
                key={`ghost-${ghost.id}`}
                suggestion={ghost}
                onClick={effectiveOnGhostCardClick}
              />
            ))}
          </div>
        )}
        </div>

      {/* Apply/Cancel buttons for pending reorder */}
      {hasPendingReorder && (
        <div className={styles.pendingReorderBar}>
          <span className={styles.pendingReorderLabel}>Unsaved priority changes</span>
          <button
            className={styles.pendingReorderApply}
            onClick={() => onApplyReorder?.(driver.unitnumber)}
          >
            Apply Prioritization
          </button>
          <button
            className={styles.pendingReorderCancel}
            onClick={() => onCancelReorder?.(driver.unitnumber)}
          >
            Cancel
          </button>
        </div>
      )}
        </div>{/* end dayNavLaneContent */}

        {/* Right nav bar (next day) */}
        <div
          className={styles.dayNavBar}
          onClick={(e) => { e.stopPropagation(); handleDayNav(1); }}
          title="Next day"
        >
          <ChevronRightIcon className={styles.dayNavBarIcon} />
        </div>
      </div>{/* end dayCyclingWrapper */}
    </div>
  );
}

export default DriverLane;
